/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useTranslation } from "react-i18next";
import { Field, formValueSelector, reduxForm } from "redux-form";

import FormField from "components/FormField";
import FormSelector from "components/FormSelector";
import FormTimeSelector from "components/FormTimeSelector";
import FormCheckbox from "components/FormCheckbox";
import FormSelectorMulti from "components/FormSelectorMulti";

import { newAlertMessage } from "store/actions/globalAlertActions";
import { uploadProjectImage, deleteProjectImage } from "store/actions/mainUserActions";
import UploadImageInput from "components/UploadImageInput/UploadImageInput";
import amenityUtils from "./amenityUtils";

const { isPositiveInteger, getMinutesFromHHMM, currencyOptions } = amenityUtils;

const validate = (values) => {
  const errors = {};

  const required = [
    "name",
    "startTime",
    "endTime",
    "amenityDuration",
    "minPreviousTime",
    "minCancelTime",
  ];

  required.forEach((f) => {
    if (values[f] === undefined || values[f] === null || values[f] === "") {
      errors[f] = i18n.t("Required");
    }
  });

  if (!values.availableDays || values.availableDays.length === 0) {
    errors.availableDays = i18n.t("Required");
  }

  if (!isPositiveInteger(values.cost)) {
    errors.cost = i18n.t("costPositive");
  }

  if (values.resourcesLimited && !isPositiveInteger(values.resourcesLimitNumber)) {
    errors.resourcesLimitNumber = i18n.t("requiredPositiveInteger");
  }

  if (values.clientLimit && !isPositiveInteger(values.clientLimit)) {
    errors.clientLimit = i18n.t("requiredPositiveInteger");
  }

  const clientLimitNumber = Number(values.clientLimit);
  const resourcesLimitNumber = Number(values.resourcesLimitNumber);

  if (
    values.resourcesLimited &&
    isPositiveInteger(values.clientLimit) &&
    isPositiveInteger(values.resourcesLimitNumber) &&
    clientLimitNumber > resourcesLimitNumber
  ) {
    errors.clientLimit = i18n.t("clientLimitMax");
  }

  const start = getMinutesFromHHMM(values.startTime);
  const end = getMinutesFromHHMM(values.endTime);

  if (start !== null && end !== null && start >= end) {
    errors.startTime = i18n.t("startMustBeEarlier");
    errors.endTime = i18n.t("endMustBeLater");
  }

  return errors;
};

function AmenityForm(props) {
  const {
    handleSubmit,
    pristine,
    onSubmit,
    actionType,
    resourcesLimited,
    associatedServicesEnable,
    associatedServicesOptions,
    projectId,

    actNewAlertMessage,
    actUploadProjectImage,
    actDeleteProjectImage,
    mainUserUid,

    change,

    existingImageUid,
    formImageUid,
  } = props;

  const { t } = useTranslation();
  const weekDaysOptions = amenityUtils.getWeekDaysOptions();

  const [imageData, setImageData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageMarkedForDelete, setIsImageMarkedForDelete] = useState(false);

  const buildCleanedData = (data, imageUidFinal) => ({
    ...data,
    cost: Number(data?.cost),
    resourcesLimitNumber: Number(data?.resourcesLimitNumber),
    clientLimit: Number(data?.clientLimit),
    startTime: String(data?.startTime),
    endTime: String(data?.endTime),
    amenityDuration: Number(data?.amenityDuration),
    minPreviousTime: Number(data?.minPreviousTime),
    minCancelTime: Number(data?.minCancelTime),
    availableDays: (data?.availableDays || []).map(Number),
    associatedServices: data?.associatedServices || [],
    resourcesLimited: Boolean(data?.resourcesLimited),
    autoConfirmateEnable: Boolean(data?.autoConfirmateEnable),
    associatedServicesEnable: Boolean(data?.associatedServicesEnable),
    onlyStartInEvents: Boolean(data?.onlyStartInEvents),

    imageUid: imageUidFinal || null,
  });

  const handleSave = (data) => {
    if (!projectId) {
      actNewAlertMessage({ type: "warning", title: "image", content: "missingProjectId" });
      return;
    }

    setIsSaving(true);

    if (!imageData) {
      if (isImageMarkedForDelete && existingImageUid) {
        actDeleteProjectImage({
          imageUid: existingImageUid,
          projectId,
          cb: (deletedImageUid) => {
            if (deletedImageUid) {
              onSubmit(buildCleanedData(data, null));
              setIsImageMarkedForDelete(false);
            } else {
              actNewAlertMessage({
                type: "error",
                title: "image",
                content: `${t("Delete")} ${t("unknownError")}`,
              });
            }
            setIsSaving(false);
          },
        });
        return;
      }

      const uidToPersist = data?.imageUid || existingImageUid || null;

      onSubmit(buildCleanedData(data, uidToPersist));
      setIsSaving(false);
      return;
    }

    actUploadProjectImage({
      image: imageData,
      projectId,
      cb: (imageUid) => {
        if (imageUid) {
          change("imageUid", imageUid);

          setImageData(null);

          actNewAlertMessage({
            type: "success",
            title: "image",
            content: `${t("uploadFile")} ${t("Success")}`,
          });

          onSubmit(buildCleanedData(data, imageUid));
          setIsSaving(false);
        } else {
          actNewAlertMessage({
            type: "error",
            title: "image",
            content: `${t("uploadFile")} ${t("unknownError")}`,
          });
          setIsSaving(false);
        }
      },
    });
  };

  const canSave =
    !isSaving &&
    (!pristine ||
      Boolean(isImageMarkedForDelete) ||
      Boolean(imageData) ||
      (Boolean(formImageUid) && formImageUid !== existingImageUid));

  return (
    <MDBox py={3}>
      <form onSubmit={handleSubmit(handleSave)}>
        <Grid container justifyContent="center">
          <Card id="basic-info">
            <MDBox
              height="4rem"
              bgColor="info"
              variant="gradient"
              coloredShadow="info"
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={-3}
              mx={10}
              px={2}
            >
              {t(actionType)} {t("amenity")}
            </MDBox>

            <MDBox pt={3} pb={3} px={4}>
              <Grid container spacing={4} justifyContent="center">
                <Field name="imageUid" component="input" type="hidden" />

                {/* NAME */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="name" component={FormField} label={t("name")} fullWidth />
                </Grid>

                {/* START / END */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <Field name="startTime" component={FormTimeSelector} label={t("startTime")} />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Field name="endTime" component={FormTimeSelector} label={t("endTime")} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* DURATION */}
                <Grid size={{ xs: 8 }}>
                  <Field
                    name="amenityDuration"
                    component={FormTimeSelector}
                    label={t("amenityDuration")}
                    inMinutes
                  />
                </Grid>

                {/* AVAILABLE DAYS */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field
                    name="availableDays"
                    component={FormSelectorMulti}
                    values={weekDaysOptions.map((d) => d.uid)}
                    names={weekDaysOptions}
                    label={t("availableDays")}
                  />
                </Grid>

                {/* COST + CURRENCY */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container>
                    <Grid size={{ xs: 2 }}>
                      <Field
                        sx={{ maxWidth: "50px" }}
                        name="cost"
                        component={FormField}
                        label={t("cost")}
                      />
                    </Grid>
                    <Grid size={{ xs: 2 }}>
                      <Field
                        sx={{ maxWidth: "80px" }}
                        name="currency"
                        component={FormSelector}
                        values={currencyOptions.values}
                        names={currencyOptions.names}
                        label={t("currency")}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* PREVIOUS + CANCEL */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="minPreviousTime"
                        component={FormTimeSelector}
                        enableDays
                        inMinutes
                        label={t("minPreviousTime")}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="minCancelTime"
                        component={FormTimeSelector}
                        enableDays
                        inMinutes
                        label={t("minCancelTime")}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* RESOURCES */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Field
                        name="resourcesLimited"
                        component={FormCheckbox}
                        label={t("resourcesLimited")}
                      />
                    </Grid>

                    {resourcesLimited && (
                      <Grid size={{ xs: 3 }}>
                        <Field
                          sx={{ maxWidth: "150px" }}
                          name="resourcesLimitNumber"
                          component={FormField}
                          label={t("resourcesLimitNumber")}
                        />
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Grid size={{ xs: 3 }}>
                        <Field
                          sx={{ maxWidth: "150px" }}
                          name="clientLimit"
                          component={FormField}
                          label={t("clientLimit")}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* AUTO CONFIRM + ASSOCIATED */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <Field
                        name="autoConfirmateEnable"
                        component={FormCheckbox}
                        label={t("autoConfirmateEnable")}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Field
                        name="onlyStartInEvents"
                        component={FormCheckbox}
                        label={t("onlyStartInEvents")}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Field
                        name="associatedServicesEnable"
                        component={FormCheckbox}
                        label={t("associatedServicesEnable")}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* ASSOCIATED SERVICES */}
                {associatedServicesEnable && (
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Field
                      name="associatedServices"
                      component={FormSelectorMulti}
                      values={associatedServicesOptions.map((s) => s.uid)}
                      names={associatedServicesOptions}
                      label={t("associatedServices")}
                    />
                  </Grid>
                )}

                {/* ICON NAME */}
                {/* <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="iconName" component={FormField} label={t("iconName")} />
                </Grid> */}

                {/* INFO */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <Field name="info" component={FormField} label={t("additionalInfo")} />
                </Grid>

                {/* FOTO */}
                <Grid size={{ xs: 12, md: 8 }}>
                  <UploadImageInput
                    label={t("image")}
                    helpText={`${t("onlyPng")} · ${t("max")} 1MB`}
                    inputId="amenity-img-file-input"
                    selectLabel={t("Select")}
                    deleteLabel={t("Delete")}
                    uploadPlaceholder={t("uploadFile")}
                    maxBytes={1024 * 1024}
                    projectId={projectId}
                    mainUserUid={mainUserUid}
                    existingImageUid={existingImageUid}
                    formImageUid={formImageUid}
                    onNewAlertMessage={actNewAlertMessage}
                    onSetImageData={(dataUrl) => {
                      setImageData(dataUrl);
                      if (dataUrl) setIsImageMarkedForDelete(false);
                    }}
                    onDeleteImage={(isDeleted) => setIsImageMarkedForDelete(isDeleted)}
                    onChangeImageUid={(uid) => change("imageUid", uid)}
                  />
                </Grid>

                {/* SUBMIT */}
                <Grid size={{ xs: 12 }}>
                  <MDBox display="flex" justifyContent="center" mt={2}>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="dark"
                      size="small"
                      disabled={!canSave}
                    >
                      {isSaving ? t("Saving") : t("Save")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </form>
    </MDBox>
  );
}

AmenityForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  actionType: PropTypes.string.isRequired,
  resourcesLimited: PropTypes.bool,
  associatedServicesEnable: PropTypes.bool,
  associatedServicesOptions: PropTypes.array,
  projectId: PropTypes.string,
  change: PropTypes.func.isRequired,
  existingImageUid: PropTypes.string,
  formImageUid: PropTypes.string,
  mainUserUid: PropTypes.string,
  actNewAlertMessage: PropTypes.func.isRequired,
  actUploadProjectImage: PropTypes.func.isRequired,
  actDeleteProjectImage: PropTypes.func.isRequired,
};

AmenityForm.defaultProps = {
  resourcesLimited: false,
  associatedServicesEnable: false,
  associatedServicesOptions: [],
  projectId: "",
  mainUserUid: "",
  existingImageUid: "",
  formImageUid: "",
};

const selector = formValueSelector("AmenityForm");

const mapStateToProps = (state, props) => {
  const { actionType, uids, projectId } = props;

  const amenities = state.amenitiesReducer?.amenities || {};
  const services = state.servicesReducer?.services || {};

  const associatedServicesOptions = Object.values(services)
    .filter((s) => s?.associatedReservationEnable)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .map((s) => ({ uid: s.uid, name: s.name }));

  let initial = {
    name: "",
    currency: "EUR",
    cost: 0,
    amenityDuration: 60,
    minPreviousTime: 60,
    minCancelTime: 60,
    resourcesLimited: false,
    resourcesLimitNumber: 1,
    clientLimit: 1,
    autoConfirmateEnable: false,
    associatedServicesEnable: false,
    associatedServices: [],
    onlyStartInEvents: false,
    availableDays: [0, 1, 2, 3, 4, 5, 6],
    // iconName: "",
    info: "",
    imageUid: null,
  };

  if (actionType === "edit" && uids.length === 1 && amenities[uids[0]]) {
    initial = { ...initial, ...amenities[uids[0]] };
  }

  return {
    initialValues: initial,
    resourcesLimited: selector(state, "resourcesLimited"),
    associatedServicesEnable: selector(state, "associatedServicesEnable"),
    associatedServicesOptions,
    onlyStartInEvents: selector(state, "onlyStartInEvents"),
    projectId,
    mainUserUid: state?.mainUserReducer?.uid || "",
    existingImageUid: initial?.imageUid || "",
    formImageUid: selector(state, "imageUid") || "",
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actUploadProjectImage: (data) => dispatch(uploadProjectImage(data)),
  actDeleteProjectImage: (data) => dispatch(deleteProjectImage(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "AmenityForm",
    validate,
  })(AmenityForm)
);
